'use client';

import { useCallback, useEffect, useState } from 'react';

import { resendVerificationEmail } from '@eliadmin/core/src/api/email-verify/email-verify-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

/** The API throttles the verification mail; the same wait as the design's password-reset example. */
const COOLDOWN_SECONDS = 60;

export type ResendStatus = 'idle' | 'sending' | 'sent' | 'failed';

/** „Odoslať aktivačný link znovu“: one request at a time, then a client-side countdown. */
export function useResendVerification(customerId: number | undefined) {
  const locale = useLocaleStore(state => state.currentLocale);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<ResendStatus>('idle');
  const [cooldownEnd, setCooldownEnd] = useState(0);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!cooldownEnd) return;
    const timer = window.setInterval(() => {
      const left = Math.max(Math.ceil((cooldownEnd - Date.now()) / 1000), 0);
      setRemaining(left);
      if (left === 0) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownEnd]);

  const send = useCallback(async () => {
    if (customerId === undefined) return;
    setError(null);
    setStatus('sending');
    try {
      await resendVerificationEmail({ customerId, locale });
      setStatus('sent');
      setRemaining(COOLDOWN_SECONDS);
      setCooldownEnd(Date.now() + COOLDOWN_SECONDS * 1000);
    } catch (failure) {
      setError(failure);
      setStatus('failed');
    }
  }, [customerId, locale]);

  return { status, remaining, send, error };
}

/** 59 → „0:59“. */
export function formatCountdown(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
